import React, { useEffect, useRef, useState, useCallback } from 'react'
import TrapFocus from '../TrapFocus'
import { eventTracking } from '../../utils/Tracking'
import styles from './Modal.module.css'

interface ComponentProps {
  children: React.ReactChildren | JSX.Element
  setShowModal: (showModal: boolean) => void
  sectionTitle: string | undefined
}

function Modal({ children, setShowModal, sectionTitle }: ComponentProps) {
  const [modalTopPosition, setModalTopPosition] = useState<number>(
    window.pageYOffset
  )
  const [animateModal, setAnimateModal] = useState<boolean>(false)
  const firstTabbableItemRef = useRef<HTMLButtonElement>(null)
  const lastTabbableItemRef = useRef<HTMLButtonElement>(null)
  const modalScrollerRef = useRef<HTMLDivElement>(null)
  const modalScrollerInnerRef = useRef<HTMLDivElement>(null)
  const modalInnerRef = useRef<HTMLDivElement>(null)
  const updateTopPosition = () => {
    setModalTopPosition(window.pageYOffset)
  }

  const closeModal = useCallback(() => {
    eventTracking({
      event: 'bp-global-energy-outlook-interaction',
      eventAction: 'click',
      eventCategory: sectionTitle,
      eventLabel: 'Modal - close',
      eventUrl: window.location.href,
    })
    setAnimateModal(false)
    setTimeout(() => setShowModal(false), 1000)
  }, [sectionTitle, setShowModal])

  useEffect(() => {
    if (modalScrollerRef.current && modalScrollerInnerRef.current) {
      let percentageScrolled = 0
      modalScrollerRef.current.addEventListener('scroll', () => {
        const scrollTop = modalScrollerRef?.current?.scrollTop ?? 0
        const windowHeight = modalScrollerRef?.current?.offsetHeight ?? 0
        const scrollerHeight = modalScrollerInnerRef?.current?.offsetHeight ?? 0
        const percentage = (scrollTop / (scrollerHeight - windowHeight)) * 100
        const percentageRoundedToNearest25 = Math.floor(percentage / 25) * 25

        if (percentageRoundedToNearest25 > percentageScrolled) {
          percentageScrolled = percentageRoundedToNearest25
          console.log('percentageScrolled', percentageScrolled)
          eventTracking({
            event: 'bp-global-energy-outlook-interaction',
            eventAction: 'scrollDepth',
            eventCategory: sectionTitle,
            eventLabel: `${percentageRoundedToNearest25}%`,
            eventUrl: window.location.href,
          })
        }
      })
    }
  }, [sectionTitle])

  useEffect(() => {
    let timeout: NodeJS.Timeout
    setTimeout(() => {
      setAnimateModal(true)
      timeout = setTimeout(() => {
        const firstTabbableItem: HTMLButtonElement | null =
          firstTabbableItemRef.current
        if (firstTabbableItem) {
          firstTabbableItem.focus()
        }
      }, 1000)
    }, 0)

    const closeModalOnEscape = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') {
        closeModal()
      }
    }

    window.addEventListener('keydown', closeModalOnEscape)
    window.addEventListener('closeModal', closeModal, false)
    window.addEventListener('resize', updateTopPosition)
    return () => {
      clearTimeout(timeout)
      window.removeEventListener('keydown', closeModalOnEscape)
      window.removeEventListener('closeModal', closeModal)
      window.removeEventListener('resize', updateTopPosition)
    }
  }, [closeModal])

  const CrossIcon = () => (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='18'
      height='18'
      viewBox='0 0 18 18'
    >
      <g fill='#007F00' fillRule='evenodd'>
        <rect
          width='23.538'
          height='1.385'
          x='-2.769'
          y='8.308'
          transform='rotate(45 9 9)'
        />
        <rect
          width='23.538'
          height='1.385'
          x='-2.769'
          y='8.308'
          transform='rotate(-45 9 9)'
        />
      </g>
    </svg>
  )

  return (
    <div className={`eo-modal ${styles.modal}`}>
      <TrapFocus wrapperRef={modalInnerRef} />
      <div
        className={`${styles['modal-overlay']} ${
          animateModal
            ? styles['modal-animate-in']
            : styles['modal-animate-out']
        }`}
        onClick={closeModal}
      ></div>
      <div
        className={`${styles['modal-wrapper']} ${
          animateModal
            ? styles['modal-animate-in']
            : styles['modal-animate-out']
        }`}
        ref={modalInnerRef}
        style={{ top: modalTopPosition }}
      >
        <button
          ref={firstTabbableItemRef}
          className={styles['modal-close-button']}
          title='Close Modal'
          onClick={closeModal}
        >
          <CrossIcon />
        </button>
        <div className={styles['modal-inner-wrapper']} ref={modalScrollerRef}>
          <div className={styles['modal-inner']} ref={modalScrollerInnerRef}>
            {children}
          </div>
        </div>
        <button
          ref={lastTabbableItemRef}
          className={styles['modal-close-button']}
          title='Close Modal'
          onClick={closeModal}
        >
          <CrossIcon />
        </button>
      </div>
    </div>
  )
}

export default Modal
